import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import {
  MEDIA_ACTIONS_CLASSES,
  MEDIA_READ_ONLY_CLASSES,
  MEDIA_SHELL_CLASSES,
} from '../media.tokens';

import { type MediaShellProps } from './MediaShell.types';

/**
 * The frame every media control sits in: the column, the actions row, the
 * read-only notice, the refusal line, and — the part that has to be shared —
 * **exactly one** `role="status"` region.
 *
 * One live region per control, never two: two regions in one control means two
 * things announcing at once and a screen reader picking whichever it likes.
 * `ScalarShell`, `StructuredShell` and `SelectionPanel` all carry the same rule,
 * and the walk asserts it by counting `[data-media-control]` against the regions
 * inside it.
 *
 * Presentation only (U5): every decision arrives as a prop.
 */
export function MediaShell({
  role,
  id,
  className,
  label,
  'aria-describedby': describedBy,
  isReadOnly = false,
  children,
  actions,
  error,
  announcement,
}: MediaShellProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div
      id={id}
      // Marks one whole control, so the walk can assert "exactly one live region
      // per control" rather than counting them across a page of several.
      data-media-control
      role={role}
      // A `group` needs naming; the attribute follows the role so a shell used
      // without one never invents a second accessible name.
      aria-label={role === undefined ? undefined : label}
      aria-describedby={role === undefined ? undefined : describedBy}
      className={cn(MEDIA_SHELL_CLASSES, className)}
    >
      {children}

      {actions === undefined ? null : <div className={MEDIA_ACTIONS_CLASSES}>{actions}</div>}

      {isReadOnly ? (
        <p className={MEDIA_READ_ONLY_CLASSES}>
          <Lock aria-hidden className="h-4 w-4 shrink-0" />
          {t('media.readOnly')}
        </p>
      ) : null}

      {error}

      {/*
        Always mounted, never conditionally rendered: a live region added to the
        DOM at the same moment it gains text is frequently not announced at all,
        because the observer has nothing to observe a change ON.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
